import { useState } from 'react';

export default function Home() {
  const [activeTab, setActiveTab] = useState('봉안당');
  const [showConsultForm, setShowConsultForm] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    facility: '봉안당',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const facilities = [
    {
      name: '봉안당',
      description: '실내 봉안시설로 사계절 쾌적한 환경에서 추모가 가능합니다.',
      features: ['24시간 추모 가능', '냉난방 완비', '개별 봉안함', '추모용품 보관'],
      price: '분양가 문의'
    },
    {
      name: '봉안묘',
      description: '개별 묘역으로 전통적인 추모 방식을 제공합니다.',
      features: ['개별 묘역', '자연석 묘비', '조경 관리', '추모로 완비'],
      price: '분양가 문의'
    },
    {
      name: '수목장',
      description: '자연친화적인 추모 방식으로 나무와 함께하는 영면의 공간입니다.',
      features: ['친환경 안장', '수목 관리', '자연 조경', '가족묘 조성'],
      price: '분양가 문의'
    }
  ];

  const galleryImages = [
    {
      url: 'https://static.readdy.ai/image/bbaff965f32a14511ae8561f7fce428b/e28b0e909549580ef3e3b743288f137d.jfif',
      title: '메모리얼파크 전경'
    },
    {
      url: 'https://static.readdy.ai/image/bbaff965f32a14511ae8561f7fce428b/994e68fa28de99f993a6a7136ae9b67e.jfif',
      title: '봉안당 건물'
    },
    {
      url: 'https://static.readdy.ai/image/bbaff965f32a14511ae8561f7fce428b/4060018a9d6753e1345c4b08dcd54845.jfif',
      title: '봉안당 로비'
    },
    {
      url: 'https://static.readdy.ai/image/bbaff965f32a14511ae8561f7fce428b/f4162ddf3fefd91261366795bff0a593.jfif',
      title: '봉안당 내부'
    },
    {
      url: 'https://static.readdy.ai/image/bbaff965f32a14511ae8561f7fce428b/86f8b79378beb3a8a37263dfd1dce1cc.jfif',
      title: '봉안묘역 전경'
    },
    {
      url: 'https://static.readdy.ai/image/bbaff965f32a14511ae8561f7fce428b/4222fdf69a272120806bce43eddf007a.jfif',
      title: '가족묘'
    },
    {
      url: 'https://static.readdy.ai/image/bbaff965f32a14511ae8561f7fce428b/9275f09b3731ed64dad088100c255149.jfif',
      title: '휴게시설'
    },
    {
      url: 'https://static.readdy.ai/image/bbaff965f32a14511ae8561f7fce428b/166d3db6656dbc6aa1e7c68aabe49531.jfif',
      title: '산책로'
    },
    {
      url: 'https://static.readdy.ai/image/bbaff965f32a14511ae8561f7fce428b/3d3145e3d581ce1e2f7a8ffd3493ab6e.jfif',
      title: '주차장'
    }
  ];

  const openImageModal = (imageUrl: string) => {
    setSelectedImage(imageUrl);
  };

  const closeImageModal = () => {
    setSelectedImage(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.phone) {
      setSubmitMessage('이름과 연락처는 필수 입력 항목입니다.');
      return;
    }

    if (formData.message.length > 500) {
      setSubmitMessage('문의내용은 500자 이내로 입력해주세요.');
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const formDataToSend = new URLSearchParams();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('facility', formData.facility);
      formDataToSend.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d3q8tb4nmar9en36nmcg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitMessage('상담신청이 성공적으로 접수되었습니다. 빠른 시일 내에 연락드리겠습니다.');
        setFormData({
          name: '',
          phone: '',
          email: '',
          facility: '봉안당',
          message: ''
        });
      } else {
        setSubmitMessage('신청 중 오류가 발생했습니다. 다시 시도해주세요.');
      }
    } catch (error) {
      setSubmitMessage('신청 중 오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16">
            <div className="flex items-center space-x-3">
              <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-green-800" style={{ fontFamily: '"Pacifico", serif' }}>
                아산메모리얼파크
              </h1>
            </div>
            <nav className="hidden md:flex space-x-4 lg:space-x-8">
              <a href="#about" className="text-sm lg:text-base text-gray-700 hover:text-green-600 font-medium">추모공원 소개</a>
              <a href="#facilities" className="text-sm lg:text-base text-gray-700 hover:text-green-600 font-medium">시설안내</a>
              <a href="#gallery" className="text-sm lg:text-base text-gray-700 hover:text-green-600 font-medium">갤러리</a>
              <a href="#location" className="text-sm lg:text-base text-gray-700 hover:text-green-600 font-medium">오시는길</a>
            </nav>
            <button
              onClick={() => setShowConsultForm(true)}
              className="bg-green-600 text-white px-4 sm:px-6 py-1.5 sm:py-2 text-sm sm:text-base rounded-full hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              상담신청
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative min-h-[70vh] sm:min-h-[80vh] lg:h-screen flex items-center justify-center bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url('https://static.readdy.ai/image/bbaff965f32a14511ae8561f7fce428b/e28b0e909549580ef3e3b743288f137d.jfif')`
        }}>
        <div className="text-center text-white max-w-4xl mx-auto px-4">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 sm:mb-6 leading-tight">
            <span className="block">품격있는 <strong>아산 추모공원</strong></span>
            <span className="block text-green-300 mt-2"><strong>아산메모리얼파크</strong></span>
          </h1>
          <p className="text-base sm:text-lg md:text-xl lg:text-2xl mb-6 sm:mb-8 opacity-90 px-4">
            <span className="block">자연과 조화를 이룬 아름다운 환경에서</span>
            <span className="block mt-1">소중한 분을 영원히 모시는 공간</span>
          </p>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-12 sm:py-16 lg:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-12 lg:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 sm:mb-4"><strong>아산 추모공원</strong> 소개</h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              <strong>아산메모리얼파크</strong>는 충남 아산시 송악면에 위치한 현대적이고 품격 있는 <strong>아산 추모공원</strong>입니다.
              <strong>봉안당</strong>, <strong>봉안묘</strong>, <strong>수목장</strong> 등 다양한 안장시설을 갖추고 있습니다.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div>
              <img
                src="https://static.readdy.ai/image/bbaff965f32a14511ae8561f7fce428b/171ca54f688b98c63d997c9ec3713dbe.jfif"
                alt="아산메모리얼파크 전경 - 아산 추모공원"
                title="아산메모리얼파크 전경 - 아산 추모공원"
                className="rounded-lg shadow-lg w-full h-64 sm:h-72 lg:h-80 object-cover object-top"
              />
            </div>
            <div className="space-y-4 sm:space-y-6">
              <div className="flex items-start space-x-3 sm:space-x-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-leaf-line text-green-600 text-lg sm:text-xl"></i>
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-1 sm:mb-2">자연친화적 환경</h3>
                  <p className="text-sm sm:text-base text-gray-600">아름다운 자연 속에서 고인을 모시고 유족들이 편안하게 추모할 수 있는 공간을 제공합니다. <strong>아산 추모공원</strong>의 자연친화적 환경은 마음의 평안을 가져다줍니다.</p>
                </div>
              </div>

              <div className="flex items-start space-x-3 sm:space-x-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-building-line text-green-600 text-lg sm:text-xl"></i>
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-1 sm:mb-2">현대적 시설</h3>
                  <p className="text-sm sm:text-base text-gray-600">최신 시설과 전통적 가치가 조화된 품격 있는 추모 공간을 조성하였습니다. <strong>봉안당</strong>, <strong>봉안묘</strong>, <strong>수목장</strong> 모든 시설이 현대적으로 설계되었습니다.</p>
                </div>
              </div>

              <div className="flex items-start space-x-3 sm:space-x-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-heart-line text-green-600 text-lg sm:text-xl"></i>
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-1 sm:mb-2">정성스러운 관리</h3>
                  <p className="text-sm sm:text-base text-gray-600">전문 관리팀이 365일 정성스럽게 관리하여 항상 깨끗하고 아름다운 환경을 유지합니다. <strong>아산메모리얼파크</strong>는 최고의 관리 서비스를 제공합니다.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Facilities Section */}
      <section id="facilities" className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-12 lg:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">시설 안내</h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 px-4">다양한 안치 방식으로 개인의 선택과 가족의 뜻에 맞는 최적의 추모 서비스를 제공합니다. <strong>봉안당</strong>, <strong>봉안묘</strong>, <strong>수목장</strong> 중에서 선택하실 수 있습니다.</p>
          </div>

          <div className="flex justify-center mb-6 sm:mb-8 overflow-x-auto">
            <div className="bg-gray-100 p-1 rounded-full inline-flex">
              {facilities.map((facility) => (
                <button
                  key={facility.name}
                  onClick={() => setActiveTab(facility.name)}
                  className={`px-4 sm:px-6 py-1.5 sm:py-2 text-sm sm:text-base rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                    activeTab === facility.name
                      ? 'bg-green-600 text-white'
                      : 'text-gray-600 hover:text-green-600'
                  }`}
                >
                  {facility.name}
                </button>
              ))}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div>
              <img
                src={
                  activeTab === '봉안당'
                    ? 'https://static.readdy.ai/image/bbaff965f32a14511ae8561f7fce428b/9566a73adf0458140b10aac75f7ebd39.jfif'
                    : activeTab === '봉안묘'
                    ? 'https://static.readdy.ai/image/bbaff965f32a14511ae8561f7fce428b/a67e0ed21013dbedcf546d88c63f07b2.jfif'
                    : 'https://static.readdy.ai/image/bbaff965f32a14511ae8561f7fce428b/6378f358c21bcdf23912b827402dbdd1.jfif'
                }
                alt={`${activeTab} - 아산메모리얼파크`}
                title={`${activeTab} - 아산메모리얼파크`}
                className="rounded-lg shadow-lg w-full h-64 sm:h-72 lg:h-80 object-cover object-top"
              />
            </div>
            <div>
              {facilities.map((facility) =>
                activeTab === facility.name && (
                  <div key={facility.name} className="space-y-4 sm:space-y-6">
                    <div>
                      <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 sm:mb-4"><strong>{facility.name}</strong></h3>
                      <p className="text-base sm:text-lg text-gray-600 mb-4 sm:mb-6">{facility.description}</p>
                    </div>

                    <div className="space-y-2 sm:space-y-3">
                      <h4 className="text-lg sm:text-xl font-semibold text-gray-900">
                        <a id={`${facility.name}-features`}>주요 특징</a>
                      </h4>
                      {facility.features.map((feature, index) => (
                        <div key={index} className="flex items-center space-x-2 sm:space-x-3">
                          <i className="ri-check-line text-green-600 text-base sm:text-lg"></i>
                          <span className="text-sm sm:text-base text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>

                    <div className="bg-green-50 p-4 sm:p-6 rounded-lg">
                      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-0">
                        <span className="text-base sm:text-lg font-semibold text-gray-900">{facility.price}</span>
                        <button
                          onClick={() => setShowConsultForm(true)}
                          className="bg-green-600 text-white px-5 sm:px-6 py-2 text-sm sm:text-base rounded-full hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer w-full sm:w-auto"
                        >
                          상담 신청
                        </button>
                      </div>
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="py-12 sm:py-16 lg:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-12 lg:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">갤러리</h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 px-4"><strong>아산메모리얼파크</strong>의 아름다운 모습을 확인해보세요. <strong>아산 추모공원</strong>의 다양한 시설과 환경을 사진으로 만나보실 수 있습니다.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {galleryImages.map((image, index) => {
              const imageUrl = image.url || `https://public.readdy.ai/ai/img_res/948a13f1eb402f8ff16472e217608fb6.jpg$%7BencodeURIComponent%28%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20image.description%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%29%7D%2C%20Korean%20memorial%20park%2C%20peaceful%20atmosphere%2C%20professional%20photography&width=400&height=300&seq=gallery-${index}&orientation=landscape`;
              return (
                <div
                  key={index}
                  className="group cursor-pointer"
                  onClick={() => openImageModal(imageUrl)}
                >
                  <img
                    src={imageUrl}
                    alt={`${image.title} - 아산메모리얼파크`}
                    title={`${image.title} - 아산메모리얼파크`}
                    className="w-full h-48 sm:h-56 lg:h-64 object-cover object-top rounded-lg shadow-md group-hover:shadow-xl transition-shadow"
                  />
                  <div className="mt-2 sm:mt-3 text-center">
                    <h4 className="text-base sm:text-lg font-semibold text-gray-900">
                      <a id={`gallery-${index}`}>{image.title}</a>
                    </h4>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="text-center mt-8 sm:mt-10 lg:mt-12">
            <div className="bg-white p-6 sm:p-8 rounded-lg shadow-md inline-block max-w-lg mx-4">
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">
                <a id="visit-consultation">현장 방문 상담</a>
              </h3>
              <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
                직접 방문하셔서 <strong>아산 추모공원</strong> 시설을 둘러보시고<br />
                전문 상담을 받으실 수 있습니다.
              </p>
              <a
                href="tel:010-2874-7740"
                className="bg-green-600 text-white px-6 sm:px-8 py-2.5 sm:py-3 rounded-full text-base sm:text-lg font-semibold hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer inline-block"
              >
                <i className="ri-phone-line mr-2"></i>
                방문 예약: 010-2874-7740
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section id="location" className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-12 lg:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
              <a id="location-info">오시는 길</a>
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600">충남 아산시 송악면 외암로 55-15</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
            <div>
              <img
                src="https://static.readdy.ai/image/bbaff965f32a14511ae8561f7fce428b/b8a5ce1c9f907aa214aea0c23d64890a.jfif"
                alt="아산메모리얼파크 위치 지도 - 충남 아산시 송악면 외암로 55-15"
                title="아산메모리얼파크 위치 지도 - 충남 아산시 송악면 외암로 55-15"
                className="rounded-lg shadow-lg w-full h-64 sm:h-80 lg:h-96 object-cover object-center"
              />
              <div className="mt-4 sm:mt-6 p-4 sm:p-6 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-start space-x-3 sm:space-x-4">
                  <div className="w-8 h-8 flex items-center justify-center">
                    <i className="ri-map-pin-fill text-green-600 text-xl sm:text-2xl"></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-gray-900 text-base sm:text-lg mb-1 sm:mb-2">위치</h3>
                    <p className="text-sm sm:text-base text-gray-700 font-medium">충남 아산시 송악면 외암로<br />55-15</p>
                    <p className="text-xs sm:text-sm text-gray-600 mt-1"><strong>아산메모리얼파크</strong></p>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-6 sm:space-y-8">
              <div>
                <h3 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4">
                  <a id="contact-info">주소 및 연락처</a>
                </h3>
                <div className="space-y-3 sm:space-y-4">
                  <div className="flex items-start space-x-2 sm:space-x-3">
                    <i className="ri-map-pin-line text-green-600 text-lg sm:text-xl mt-1"></i>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm sm:text-base">주소</p>
                      <p className="text-sm sm:text-base text-gray-600">충남 아산시 송악면 외암로 55-15</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-2 sm:space-x-3">
                    <i className="ri-phone-line text-green-600 text-lg sm:text-xl mt-1"></i>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm sm:text-base">상담 전화</p>
                      <a href="tel:010-2874-7740" className="text-sm sm:text-base text-green-600 hover:text-green-700 cursor-pointer">
                        010-2874-7740
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-2 sm:space-x-3">
                    <i className="ri-time-line text-green-600 text-lg sm:text-xl mt-1"></i>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm sm:text-base">상담 시간</p>
                      <p className="text-sm sm:text-base text-gray-600">평일 09:00 - 18:00</p>
                      <p className="text-sm sm:text-base text-gray-600">주말 09:00 - 17:00</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4">
                  <a id="transportation-guide">교통 안내</a>
                </h3>
                <div className="space-y-2 sm:space-y-3">
                  <div className="flex items-start space-x-2 sm:space-x-3">
                    <i className="ri-car-line text-green-600 text-lg sm:text-xl mt-1"></i>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm sm:text-base">자가용</p>
                      <p className="text-sm sm:text-base text-gray-600">경부고속도로 천안IC → 아산방향 → 송악면</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-2 sm:space-x-3">
                    <i className="ri-bus-line text-green-600 text-lg sm:text-xl mt-1"></i>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm sm:text-base">대중교통</p>
                      <p className="text-sm sm:text-base text-gray-600">아산역 → 송악면행 버스 이용</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-green-600">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4 sm:mb-6">
            지금 바로<br /><strong>아산 추모공원</strong> 상담 받아보세요
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-green-100 mb-6 sm:mb-8">
            <strong>아산메모리얼파크</strong> 전문 상담사가<br />
            친절하게 안내해드립니다
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4">
            <button
              onClick={(): any => setShowConsultForm(true)}
              className="bg-white text-green-600 px-6 sm:px-8 py-3 sm:py-4 rounded-full text-base sm:text-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              온라인 상담 신청
            </button>
            <a
              href="tel:010-2874-7740"
              className="bg-green-700 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full text-base sm:text-lg font-semibold hover:bg-green-800 transition-colors whitespace-nowrap cursor-pointer"
              rel="nofollow"
            >
              <i className="ri-phone-line mr-2"></i>
              전화 상담: 010-2874-7740
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 sm:py-10 lg:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            <div>
              <div className="flex items-center space-x-3 mb-3 sm:mb-4">
                <h3 className="text-xl sm:text-2xl font-bold" style={{ fontFamily: '"Pacifico", serif' }}>
                  아산메모리얼파크
                </h3>
              </div>
              <p className="text-sm sm:text-base text-gray-300 mb-3 sm:mb-4">
                품격있는 <strong>아산 추모공원</strong>으로 소중한 분을 영원히 모시는 공간입니다.
              </p>
              <div className="flex space-x-4">
                <a href="tel:010-2874-7740" className="text-gray-300 hover:text-white cursor-pointer">
                  <i className="ri-phone-line text-lg sm:text-xl"></i>
                </a>
                <a href="#location" className="text-gray-300 hover:text-white cursor-pointer">
                  <i className="ri-map-pin-line text-lg sm:text-xl"></i>
                </a>
              </div>
            </div>

            <div>
              <h4 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">시설 안내</h4>
              <ul className="space-y-1.5 sm:space-y-2 text-sm sm:text-base text-gray-300">
                <li><strong>봉안당</strong></li>
                <li><strong>봉안묘</strong></li>
                <li><strong>수목장</strong></li>
              </ul>
            </div>

            <div className="sm:col-span-2 lg:col-span-1">
              <h4 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">연락처</h4>
              <div className="space-y-1.5 sm:space-y-2 text-sm sm:text-base text-gray-300">
                <p>충남 아산시 송악면 외암로 55-15</p>
                <p>상담전화: 010-2874-7740</p>
                <p>평일 09:00-18:00, 주말 09:00-17:00</p>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-700 mt-6 sm:mt-8 pt-6 sm:pt-8 text-center text-xs sm:text-sm text-gray-400">
            <p>&copy; 2024 <strong>아산메모리얼파크</strong>. All rights reserved. |
              <a href="https://readdy.ai/?origin=logo" className="hover:text-white cursor-pointer ml-1">
                Website Builder
              </a>
            </p>
          </div>
        </div>
      </footer>

      {/* Consultation Form Modal */}
      {showConsultForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-5 sm:p-6">
              <div className="flex justify-between items-center mb-5 sm:mb-6">
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900">상담 신청</h3>
                <button
                  onClick={() => setShowConsultForm(false)}
                  className="text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className="ri-close-line text-xl sm:text-2xl"></i>
                </button>
              </div>

              <form id="consultation-form" data-readdy-form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    이름 *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                    placeholder="성함을 입력해주세요"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                    연락처 *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                    placeholder="연락 가능한 번호를 입력해주세요"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    이메일
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                    placeholder="이메일 주소를 입력해주세요"
                  />
                </div>

                <div>
                  <label htmlFor="facility" className="block text-sm font-medium text-gray-700 mb-1">
                    관심 시설
                  </label>
                  <div className="relative">
                    <select
                      id="facility"
                      name="facility"
                      value={formData.facility}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm appearance-none bg-white"
                    >
                      <option value="봉안당">봉안당</option>
                      <option value="봉안묘">봉안묘</option>
                      <option value="수목장">수목장</option>
                      <option value="기타">기타</option>
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                      <i className="ri-arrow-down-s-line text-gray-400"></i>
                    </div>
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                    문의내용
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={4}
                    maxLength={500}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm resize-none"
                    placeholder="궁금한 사항이나 요청사항을 입력해주세요 (최대 500자)"
                  />
                  <div className="text-right text-xs text-gray-500 mt-1">
                    {formData.message.length}/500
                  </div>
                </div>

                {submitMessage && (
                  <div className={`p-3 rounded-md text-sm ${
                    submitMessage.includes('성공')
                      ? 'bg-green-50 text-green-700 border border-green-200'
                      : 'bg-red-50 text-red-700 border border-red-200'
                  }`}>
                    {submitMessage}
                  </div>
                )}

                <div className="flex gap-3 pt-3 sm:pt-4">
                  <button
                    type="button"
                    onClick={() => setShowConsultForm(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 text-sm sm:text-base rounded-md hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    취소
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 px-4 py-2 bg-green-600 text-white text-sm sm:text-base rounded-md hover:bg-green-700 disabled:bg-gray-400 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {isSubmitting ? '신청 중...' : '상담 신청'}
                  </button>
                </div>

                <div className="text-center pt-3 sm:pt-4 border-t border-gray-200">
                  <p className="text-sm text-gray-600 mb-2 sm:mb-3">또는 직접 전화 상담</p>
                  <a
                    href="tel:010-2874-7740"
                    className="bg-green-100 text-green-700 px-5 sm:px-6 py-2 rounded-full text-sm font-medium hover:bg-green-200 transition-colors whitespace-nowrap cursor-pointer inline-block"
                  >
                    <i className="ri-phone-line mr-2"></i>
                    010-2874-7740
                  </a>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Image Modal */}
      {selectedImage && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50" onClick={closeImageModal}>
          <div className="relative max-w-4xl max-h-[90vh] w-full">
            <button
              onClick={closeImageModal}
              className="absolute top-2 right-2 sm:top-4 sm:right-4 text-white hover:text-gray-300 cursor-pointer z-10"
            >
              <i className="ri-close-line text-2xl sm:text-3xl"></i>
            </button>
            <img
              src={selectedImage}
              alt="확대된 갤러리 이미지"
              className="w-full h-full object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
}
